import classNames from 'classnames';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Dropdown from '../../../../components/molecules/Dropdown';
import { WarningMessage } from '../../../../components/widgets';
import { getSectionAccessType } from '../../../../selectors/navigation';
import { SECTION_ACCESS_TYPES, SECTIONS } from '../../../../utils/consts';
import {
    eventShape,
    organizationOptionsShape,
    ParameterTypes,
    partnerShape,
    siteShape,
    templateShape,
    TemplateTypes,
} from '../../types';
import css from '../PartnerConfigurationShelf.css';
import { POSTBACK_FILTERS_TYPES } from '../advancedPostbackSettings/PostbackFilters';
import { getCheckboxCellProps } from '../tableCellsUtils';
import { getUsedParameters } from '../utils';
import { mergeEventsWithConfigured } from './CustomEventPostbacks';
import EventPostbacksData from './EventPostbacksData';
import { getDefaultEventPostback, getDropdownOption } from './EventPostbacksTable';
import revenueEventPostbackCss from './RevenueEventPostbacks.css';

export const REVENUE_SELECTION_DATA_TEST_ID = 'revenue-postbacks-selection-dropdown';
export const ADMON_USER_LEVEL_REVENUE = '__ADMON_USER_LEVEL_REVENUE__';
export const REVENUE = '__REVENUE__*';
export const NOT_SUPPORTED = getDropdownOption('Not supported');
export const DEFAULT_REVENUE_POSTBACK_OPTION = getDropdownOption('Select');
export const REVENUE_TABLE_DATA_TEST_ID = 'revenue-postbacks-table';

export const RevenuePostbackOptions = {
    SEND_ALL_ADMON: 'SEND_ALL_ADMON',
    SEND_ALL_REVENUE: 'SEND_ALL_REVENUE',
    MANUALLY_SELECT: 'MANUALLY_SELECT',
};

const getSelectedRevenueOption = (revenueEventPostbacks, revenueEvents) => {
    if (!revenueEventPostbacks) {
        return null;
    }

    if (revenueEventPostbacks.length !== 1) {
        return RevenuePostbackOptions.MANUALLY_SELECT;
    }

    const event = revenueEvents.find(({ id }) => id === revenueEventPostbacks[0].eventId);
    if (event?.displayName === REVENUE) {
        return RevenuePostbackOptions.SEND_ALL_REVENUE;
    }

    if (event?.displayName === ADMON_USER_LEVEL_REVENUE) {
        return RevenuePostbackOptions.SEND_ALL_ADMON;
    }

    return RevenuePostbackOptions.MANUALLY_SELECT;
};

const RevenueEventPostbacks = ({
    partner,
    site,
    templates = [],
    events = [],
    isReadonly = false,
    orgOptions,
    availableFilters,
}) => {
    const sectionAccessType = useSelector(
        getSectionAccessType('new_partner_config', SECTIONS.PARTNER_CONFIGURATION_IN_APP_EVENTS_POSTBACK)
    );
    const hasViewAccess = sectionAccessType === SECTION_ACCESS_TYPES.VIEW;
    const disabled = hasViewAccess || isReadonly;
    const { values, setValues, errors, touched, setTouched } = useFormikContext();

    // Filter to revenue events from allowed events
    const allowedRevenueEvents = events.filter(event => event.revenueEvent);

    // Merge configured events with allowed events - configured events not in allowed list are marked as _disallowed
    const revenueEvents = useMemo(
        () => mergeEventsWithConfigured(allowedRevenueEvents, values.revenueEventPostbacks, true),
        [allowedRevenueEvents, values.revenueEventPostbacks]
    );

    const revenueOption = getSelectedRevenueOption(values.revenueEventPostbacks, revenueEvents);
    const revenueDropdownOption = revenueOption
        ? getDropdownOption(`STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.${revenueOption}`, revenueOption)
        : null;

    const [selectedRevenueOption, setSelectedRevenueOption] = useState(revenueDropdownOption);

    const revenueParameters = getUsedParameters(partner, site.platform, TemplateTypes.REVENUE, [
        ParameterTypes.POSTBACK,
    ]);

    const addEmptyRevenuePostback = () => {
        setValues({ ...values, revenueEventPostbacks: [] });
        setTouched({ ...touched, revenueEventPostbacks: [] });
    };
    const setRevenuePostbacksValues = revenueEventPostbacks => setValues({ ...values, revenueEventPostbacks });
    const setTouchedRevenuePostbacks = revenueEventPostbacks => setTouched({ ...touched, revenueEventPostbacks });

    const getRevenueSelectionEvents = option => {
        const selectedOption = option?.name || selectedRevenueOption?.name;

        if (selectedOption === RevenuePostbackOptions.MANUALLY_SELECT) {
            return revenueEvents.filter(event => event.displayName !== REVENUE);
        } else if (selectedOption === RevenuePostbackOptions.SEND_ALL_REVENUE) {
            return revenueEvents.filter(event => event.displayName === REVENUE);
        } else if (selectedOption === RevenuePostbackOptions.SEND_ALL_ADMON) {
            return revenueEvents.filter(event => event.displayName === ADMON_USER_LEVEL_REVENUE);
        }

        return revenueEvents;
    };

    const revenueTableCells = [
        getCheckboxCellProps(
            'sendRevenue',
            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.EVENT_POSTBACKS_TABLE.INCLUDES_REVENUE',
            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.EVENT_POSTBACKS_TABLE.INCLUDES_REVENUE_TOOLTIP',
            {},
            false,
            { tdClassName: classNames(css.tableCell, css.checkboxCell) }
        ),
    ];

    const selectedRevenueOptions = Object.values(RevenuePostbackOptions).map(field => ({
        name: field,
        display_name: `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.${field}`,
    }));

    const postbackExtraFields = { sendRevenue: true };
    const revenueEventTemplate = templates.find(template => template.type === TemplateTypes.REVENUE);

    const onRevenuePostbackOptionChanged = selectedOption => {
        if (selectedOption === selectedRevenueOption) {
            return;
        }

        setSelectedRevenueOption(selectedOption);

        if (selectedOption.name === RevenuePostbackOptions.MANUALLY_SELECT) {
            addEmptyRevenuePostback();
            return;
        }

        const revenueSelectionEvents = getRevenueSelectionEvents(selectedOption);
        // This should be REVENUE or ADMON_USER_LEVEL_REVENUE event only due to user selection
        const event = revenueSelectionEvents[0];
        if (!event) {
            setRevenuePostbacksValues(null);
            setTouchedRevenuePostbacks([]);
            return;
        }

        setRevenuePostbacksValues([
            getDefaultEventPostback(partner, revenueEventTemplate, revenueParameters, {
                ...postbackExtraFields,
                eventId: event.id,
            }),
        ]);
    };

    // Use allowedRevenueEvents for dropdown disabled check
    const dropdownDisabled = !revenueEventTemplate || !allowedRevenueEvents.length || disabled;
    const defaultDropdownSelection = dropdownDisabled ? NOT_SUPPORTED : DEFAULT_REVENUE_POSTBACK_OPTION;

    return (
        <div
            className={css.section}
            data-field-error={errors.revenueEventPostbacks ? 'revenueEventPostbacks' : undefined}
        >
            <div className={css.title}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.REVENUE_EVENTS_POSTBACKS" />
            </div>
            <div className={revenueEventPostbackCss.topText}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.REVENUE_EVENTS_DROPDOWN_TEXT" />
            </div>
            {hasViewAccess && !isReadonly && (
                <WarningMessage
                    showIcon={false}
                    showSpacer={false}
                    type="permission"
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.VIEW_ACCESS_ONLY"
                    className={css.viewAccessMessage}
                    textContainerClass={css.viewAccessMessageTextContainer}
                />
            )}
            <Dropdown
                containerClass={revenueEventPostbackCss.revenuePostbacksOptions}
                popperClass={revenueEventPostbackCss.revenuePostbacksOptions}
                wrapperClass={revenueEventPostbackCss.dropdownWrapper}
                items={selectedRevenueOptions}
                onSelection={onRevenuePostbackOptionChanged}
                dropDownBodyTestId={REVENUE_SELECTION_DATA_TEST_ID}
                disabled={dropdownDisabled}
                selected={selectedRevenueOption || defaultDropdownSelection}
            />
            <EventPostbacksData
                configuredEvents={values.revenueEventPostbacks}
                events={getRevenueSelectionEvents()}
                allowedEvents={allowedRevenueEvents}
                template={revenueEventTemplate}
                partner={partner}
                site={site}
                availableFilters={availableFilters}
                extraTableCells={revenueTableCells}
                postbackExtraFields={postbackExtraFields}
                tableDataTestId={REVENUE_TABLE_DATA_TEST_ID}
                templateType={TemplateTypes.REVENUE}
                setPostbacks={setRevenuePostbacksValues}
                setTouched={setTouchedRevenuePostbacks}
                errors={errors.revenueEventPostbacks}
                touched={touched.revenueEventPostbacks}
                isReadonly={disabled}
                orgOptions={orgOptions}
            />
        </div>
    );
};

RevenueEventPostbacks.propTypes = {
    partner: partnerShape.isRequired,
    site: siteShape.isRequired,
    orgOptions: organizationOptionsShape.isRequired,
    templates: PropTypes.arrayOf(templateShape),
    events: PropTypes.arrayOf(eventShape),
    isReadonly: PropTypes.bool,
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
};

export default RevenueEventPostbacks;
